import VerificationStepTitle from "@/components/VereficationStore/VerificationStepTitle";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { object, string } from "yup";
import toast from "react-hot-toast";
import { Bank, BusinessVerificationStatus } from "@/types/globalTypes";
import VerificationMessage from "@/components/VereficationStore/VerificationMessage";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorMessage from "@/components/ErrorMessage";
import { FaDownload } from "react-icons/fa";
import * as Yup from "yup";
import { subYears } from "date-fns";
import { isProduction } from "@/utils/helpers";

function VerificationForm() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [banks, setBanks] = useState<Bank[]>([] as Bank[]);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const [businessVerificationStatus, setBusinessVerificationStatus] =
    useState<BusinessVerificationStatus>("not-verified");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needRefetch, setNeedRefetch] = useState(false);
  // Send files
  const [idImg, setIdImg] = useState<File | null>(null);
  const [regestrationImg, setregestrationImg] = useState<File | null>(null);
  const [ibanImg, setIbanImg] = useState<File | null>(null);
  // Images Preview
  const [idImgPreview, setIdImgPreview] = useState<string | null>(null);
  const [ibanImgPreview, setIbanImgPreview] = useState<string | null>(null);
  const [regestrationImgPreview, setRegestrationImgPreview] = useState<
    string | null
  >(null);

  const handleIDImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setIdImg(selectedFile);
      formik.setFieldValue("idImage", selectedFile);
    }
  };
  const handleRegestrationImgFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setregestrationImg(selectedFile);
      formik.setFieldValue("commericalRegistrationImage", selectedFile);
    }
  };

  const handleIbanImgFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setIbanImg(selectedFile);
      formik.setFieldValue("ibanImage", selectedFile);
    }
  };

  const validationSchema = object({
    // Text Inputs
    idNumber: string().required(
      t("verificationForm.idRequired") as string,
    ),
    dateOfBirth: Yup.date()
      .required(   t("verificationForm.ageRequirement") as string)
      .max(
        subYears(new Date(), 18),
         t("verificationForm.dobRequired") as string
      ),
    commericalRegistrationNumber: string().required(
      t("verificationForm.crRequired") as string,
    ),

    entityName: string().required(
      t("verificationForm.entityNameRequired") as string,
    ),
    bankAccountNumber: string().required(
      t("verificationForm.bankAccountRequired") as string,
    ),
    ibanNumber: string().required(
      t("verificationForm.ibanRequired") as string,
    ),

    // Images

    idImage: Yup.mixed<File>()
      .required(t("verificationForm.imageRequired") as string)
      .test(
        "fileSize",
        t("verificationForm.fileSizeTooLarge") as string,
        (value) => {
          // Limit the file size to 10MB (10 * 1024 * 1024 bytes)
          return value && value.size <= 10 * 1024 * 1024;
        },
      ),

    commericalRegistrationImage: Yup.mixed<File>()
      .required(t("verificationForm.imageRequired") as string)
      .test(
        "fileSize",
        t("verificationForm.fileSizeTooLarge")  as string,
        (value) => {
          // Limit the file size to 10MB (10 * 1024 * 1024 bytes)
          return value && value.size <= 10 * 1024 * 1024;
        },
      ),

    ibanImage: Yup.mixed<File>()
      .required(    t("verificationForm.imageRequired")  as string)
      .test(
        "fileSize",
        t("verificationForm.fileSizeTooLarge")  as string,
        (value) => {
          // Limit the file size to 10MB (10 * 1024 * 1024 bytes)
          return value && value.size <= 10 * 1024 * 1024;
        },
      ),

    // Select inputs
    bank: Yup.string().required(
      t("verificationForm.bankRequired") as string,
    ),
    entityType: Yup.string().required(
      t("verificationForm.entityTypeRequired") as string,
    ),
    verificationType: Yup.string().required(
      t("verificationForm.verificationTypeRequired") as string,
    ),
  });

  const formik = useFormik({
    initialValues: {
      idNumber: "",
      dateOfBirth: "",
      commericalRegistrationNumber: "",
      entityName: "",
      bankAccountNumber: "",
      ibanNumber: "",

      idImage: null,
      ibanImage: null,
      commericalRegistrationImage: null,

      bank: "",
      verificationType: "IBAN-certificate",
      entityType: "institution",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  async function handleSubmit() {
    const FormData = {
      type: formik.values.entityType,
      owner: {
        id: formik.values.idNumber,
        birthday: formik.values.dateOfBirth,
        attachment: idImg,
      },
      commercialRegister: {
        crn: formik.values.commericalRegistrationNumber,
        attachment: regestrationImg,
      },
      bankingInformation: {
        id: formik.values.bank,
        entityName: formik.values.entityName,
        accountNumber: formik.values.bankAccountNumber,
        iban: formik.values.ibanNumber,
        caType: "iban-certificate",
        attachment: ibanImg,
      },
    };

    const toastId = toast.loading(    t("verificationForm.sending") as string);

    try {
      const response = await axios.postForm(
        `${baseUrl}/api/dashboard/settings/business-verification-request`,
        FormData,
        {
          headers: {
            Authorization: token,
          },
        },
      );


      if (response.data.success == true) {
        toast.success(
          t("verificationForm.successRequest") as string,
        );
        setNeedRefetch(true);
      } else {
        toast.error(
          response.data.error ||
          (t("verificationForm.somethingWentWrong") as string),
        );
      }
    } catch (error: unknown) {
      console.error(error);
      toast.error(t("verificationForm.somethingWentWrong") as string);
    } finally {
      toast.dismiss(toastId);
    }
  }
  t("verificationForm.somethingWentWrong")
  const GetVerficationFormData = useCallback(async () => {
    const url = `${baseUrl}/api/dashboard/settings/business-verification-request?locale=${lang}`;
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: token,
        },
      });
      if (response.data.success == true) {
        setBusinessVerificationStatus(response.data.businessVerificationStatus);
        // if user verified fill his data
        if (response.data.businessVerificationStatus === "verified") {
          // entity type
          formik.values.entityType = response.data.businessData.type;
          // owner
          formik.values.idNumber = response.data.businessData.owner.id;
          formik.values.dateOfBirth = response.data.businessData.owner.birthday;
          setIdImgPreview(response.data.businessData.owner.attachment);

          // commercial register
          formik.values.commericalRegistrationNumber =
            response.data.businessData.commercialRegister.crn;
          setRegestrationImgPreview(
            response.data.businessData.commercialRegister.attachment,
          );

          // banking information
          formik.values.bank = response.data.businessData.bankingInformation.id;
          formik.values.entityName =
            response.data.businessData.bankingInformation.entityName;
          formik.values.bankAccountNumber =
            response.data.businessData.bankingInformation.accountNumber;
          formik.values.ibanNumber =
            response.data.businessData.bankingInformation.iban;
          setIbanImgPreview(
            response.data.businessData.bankingInformation.attachment,
          );
          formik.values.verificationType =
            response.data.businessData.bankingInformation.caType;
        }
      } else {
        setError(
          response.data.error ||
          (t("verificationForm.somethingWentWrong") as string),
        );
        return;
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setError(
          error.response?.data?.error ||
          (t("verificationForm.somethingWentWrong") as string),
        );
      } else {
        setError(t("verificationForm.somethingWentWrong") as string);
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, lang, token, t]);

  const getBanks = useCallback(async () => {
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/data/saudi-banks`,
        headers: {
          Authorization: token,
        },
      };

      const response = await axios.request(options);
      if (response.data.success == true) {
        setBanks(response.data.banks);
        formik.setFieldValue("bank", response.data.banks[0].id);
      } else {
        setError(
          response.data.error ||
          (t("verificationForm.somethingWentWrong") as string),
        );
        return;
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setError(
          error.response?.data?.error ||
          (t("verificationForm.somethingWentWrong") as string),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token, t]);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setNeedRefetch(false);

    getBanks();
    GetVerficationFormData();
  }, [GetVerficationFormData, getBanks, needRefetch]);

  if (isLoading)
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
        <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
        <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
        <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
      </div>
    );

  if (error) return <ErrorMessage msg={error} />;


    if(isProduction) return null
  

  return (
    <form
      onSubmit={formik.handleSubmit}
      id="main-form"
      className="flex flex-col gap-5 w-full lg:max-w-[70%]"
    >
      <VerificationMessage status={businessVerificationStatus} />

      {/* Steps */}
      <div className="flex size-full flex-col gap-9">
        {/* Step 1 */}
        <div className="main-border flex flex-col gap-4 p-5">
          <VerificationStepTitle
            stepNumber="1"
            title={t("verificationForm.entityType") as string}
          />

          <div className="flex flex-col gap-2">
            <label className="text-lg" htmlFor="">
              {t("verificationForm.selectEntityType") }
            </label>

            <select
              className="main-border h-10 rounded-md px-3 capitalize"
              name="entityType"
              id="entityType"
              value={formik.values.entityType}
              onChange={formik.handleChange}
            >
              <option value="institution"> {t("verificationForm.institution")}</option>
              <option value="company"> {t("verificationForm.company")}</option>
            </select>
          </div>
          {formik.errors.entityType && formik.touched.entityType && (
            <p className="text-sm text-red-600">{formik.errors.entityType}</p>
          )}
        </div>

        {/* ( Step 2 ) As A Part Of step 1 */}
        <div className="main-border flex flex-col gap-4 p-5">
          <VerificationStepTitle
            stepNumber="2"
            title={t("verificationForm.storeOwner") as string}
          />

          <div className="flex flex-col gap-2 p-2">
            <h2 className="text-lg font-semibold">
              {t("verificationForm.preVerificationCheck")}
            </h2>
            <p className="ms-2 text-base">
              {t("verificationForm.idValid")}
            </p>
          </div>

          {/* Id Number */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="idNumber">
              {t("verificationForm.idNumber")} 
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              id="storeOwner-idNumber"
              placeholder={t("verificationForm.idNumber") as string}
              className="main-border h-10 rounded-md px-3"
              type="number"
              name="idNumber"
              value={formik.values.idNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.idNumber && formik.errors.idNumber && (
              <span className="text-sm text-red-600">
                {formik.errors.idNumber}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="dateOfBirth">
              {t("verificationForm.dob")}        
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              id="storeOwner-dateOfBirth"
              className="main-border h-10 rounded-md px-3"
              type="date"
              name="dateOfBirth"
              value={formik.values.dateOfBirth}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
              <span className="text-sm text-red-600">
                {formik.errors.dateOfBirth}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="digitalIDImage">
              {t("verificationForm.digitalIDImage")}                </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              accept="image/jpg, image/jpeg, image/png application/pdf"
              id="storeOwner-digitalIDImage"
              className="file:hidden"
              type="file"
              name="idImage"
              onChange={handleIDImageFileChange}
            />

            <label
              htmlFor="storeOwner-digitalIDImage"
              className="flex h-12 w-full cursor-pointer items-center justify-center rounded-md bg-gray-200 px-4 text-gray-700 transition-all hover:bg-gray-300 focus:outline-none"
            >
              <span className="text-base font-semibold">
                {t("verificationForm.somethingWentWrong")}              </span>
            </label>
            {formik.errors.idImage && formik.touched.idImage && (
              <div className="text-sm text-red-600">
                {formik.errors.idImage}
              </div>
            )}
            {idImgPreview && (
              <div className="mt-3 flex w-full items-center gap-2">
                <a
                  href={idImgPreview}
                  target="_blank"
                  rel="noreferrer"
                  download={true}
                  className="flex items-center gap-2 rounded-md bg-sidebar px-3 py-1 text-sm text-skyblue"
                >
                  {t("verificationForm.uploadDigitalID")}        
          <FaDownload className="text-base" />
                </a>
              </div>
            )}
            <span className="mt-1 text-sm">
              {t("verificationForm.downloadPrevious")}
            </span>
          </div>
        </div>

        <div className="main-border flex flex-col gap-4 p-5">
          <VerificationStepTitle
            stepNumber="3"
            title={t("verificationForm.commericial_register") as string}
          />

          <div className="flex flex-col gap-2 p-2">
            <h2 className="text-lg font-semibold">
              {t("verificationForm.preVerificationCheck")}
            </h2>
            {/* Conditions */}
            <p className="ms-2 text-base">
              {t("verificationForm.crValid")}
            </p>
            <p className="ms-2 text-base">
              {t("verificationForm.crClear")}
            </p>
            <p className="ms-2 text-base">
              {t("verificationForm.crNoCrop")}
            </p>
            <p className="ms-2 text-base">
              {t("verificationForm.crMatch")}
            </p>
            <p className="ms-2 text-base">
              {t("verificationForm.crActivityMatch")}
            </p>
          </div>

          {/* Id Number */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label
                className="text-lg"
                htmlFor="commerical-registration-number"
              >
                {t("verificationForm.crNumberLabel")}              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              id="commerical-registration-number"
              placeholder={
                t("verificationForm.crNumberLabel") as string
              }
              className="main-border h-10 rounded-md px-3"
              type="text"
              name="commericalRegistrationNumber"
              value={formik.values.commericalRegistrationNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />

            {formik.touched.commericalRegistrationNumber &&
              formik.errors.commericalRegistrationNumber && (
                <span className="text-sm text-red-600">
                  {formik.errors.commericalRegistrationNumber}
                </span>
              )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="">
                {t("verificationForm.crPhoto")}
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              accept="image/jpg, image/jpeg, image/png application/pdf"
              id="commericalRegistrationImage"
              className="file:hidden"
              type="file"
              name="commericalRegistrationImage"
              onChange={handleRegestrationImgFileChange}
            />

            <label
              onClick={() =>
                document.getElementById("commericalRegistrationImage")?.click()
              }
              className="flex h-12 w-full cursor-pointer items-center justify-center rounded-md bg-gray-200 px-4 text-gray-700 transition-all hover:bg-gray-300 focus:outline-none"
            >
              <span className="text-base font-semibold">
                {t("verificationForm.uploadCrPhoto")}
              </span>
            </label>
            {formik.errors.commericalRegistrationImage &&
              formik.touched.commericalRegistrationImage && (
                <div className="text-sm text-red-600">
                  {formik.errors.commericalRegistrationImage}
                </div>
              )}
            {regestrationImgPreview && (
              <div className="mt-3 flex w-full items-center gap-2">
                <a
                  href={regestrationImgPreview}
                  target="_blank"
                  rel="noreferrer"
                  download={true}
                  className="flex items-center gap-2 rounded-md bg-sidebar px-3 py-1 text-sm text-skyblue"
                >
                  {t("verificationForm.downloadPrevious")}                  <FaDownload className="text-base" />
                </a>
              </div>
            )}
            <span className="mt-1 text-sm">
              {t("verificationForm.allowedImageTypes")}
            </span>
          </div>
        </div>

        <div className="main-border flex flex-col gap-4 p-5">
          <VerificationStepTitle
            stepNumber="4"
            title={t("verificationForm.bankAccount") as string}
          />

          <div className="flex flex-col gap-2 p-2">
            <h2 className="text-lg font-semibold">
              {t("verificationForm.preVerificationCheck")}
            </h2>
            {/* Conditions */}
            <p className="ms-2 text-base">
              {t("verificationForm.bankCheck1")}
            </p>
            <p className="ms-2 text-base">
              {t("verificationForm.bankCheck2")}
            </p>
          </div>

          {/* Choose Bank */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="bank">
                {t("verificationForm.selectBank")}
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <select
              className="main-border h-10 rounded-md px-3 capitalize"
              id="bank"
              name="bank"
              value={formik.values.bank}
              onChange={formik.handleChange}
            >
              {banks &&
                banks.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {lang === "ar" ? bank.nameAR : bank.nameEN}
                  </option>
                ))}
            </select>
            {formik.errors.bank && formik.touched.bank && (
              <p className="text-sm text-red-600">{formik.errors.bank}</p>
            )}
          </div>

          {/* Name of the institution */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="name-of-institution">
                {t("verificationForm.institutionName")}
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              id="entityName"
              name="entityName"
              placeholder={
                t("verificationForm.institutionName") as string
              }
              className="main-border h-10 rounded-md px-3"
              type="text"
              value={formik.values.entityName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.entityName && formik.errors.entityName && (
              <span className="text-sm text-red-600">
                {formik.errors.entityName}
              </span>
            )}
          </div>

          {/* Account Number */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="account-number">
                {t("verificationForm.accountNumber")}
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              id="bankAccountNumber"
              placeholder={t("verificationForm.accountNumber") as string}
              className="main-border h-10 rounded-md px-3"
              type="number"
              name="bankAccountNumber"
              value={formik.values.bankAccountNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.bankAccountNumber &&
              formik.errors.bankAccountNumber && (
                <span className="text-sm text-red-600">
                  {formik.errors.bankAccountNumber}
                </span>
              )}
          </div>

          {/* رقم الابيان */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="IBAN-number">
                {t("verificationForm.ibanNumber")}
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              id="ibanNumber"
              placeholder={t("verificationForm.ibanNumber") as string}
              className="main-border h-10 rounded-md px-3"
              type="text"
              name="ibanNumber"
              value={formik.values.ibanNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.ibanNumber && formik.errors.ibanNumber && (
              <span className="text-sm text-red-600">
                {formik.errors.ibanNumber}
              </span>
            )}
          </div>

          {/* نوع الابيان */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              <label
                className="text-lg"
                htmlFor="type-of-authentication-certificate"
              >
                {t("verificationForm.authCertType")}
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <select
              className="main-border h-10 rounded-md px-3 capitalize"
              id="verificationType"
              name="verificationType"
              value={formik.values.verificationType}
              onChange={formik.handleChange}
            >
              <option value="IBAN-certificate">
                {t("verificationForm.ibanCert")}
              </option>
            </select>
            {formik.errors.verificationType &&
              formik.touched.verificationType && (
                <p className="text-sm text-red-600">
                  {formik.errors.verificationType}
                </p>
              )}
          </div>

          {/* IBAN FILE */}

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <label className="text-lg" htmlFor="">
                {t("verificationForm.ibanCert")}
              </label>
              <span className="self-start text-sm text-red-600">*</span>
            </div>

            <input
              accept="image/jpg, image/jpeg, image/png, application/pdf"
              id="IBAN-cetificate-file"
              className="file:hidden"
              type="file"
              name="ibanImage"
              onChange={handleIbanImgFileChange}
            />

            <label
              htmlFor="IBAN-cetificate-file"
              className="flex h-12 w-full cursor-pointer items-center justify-center rounded-md bg-gray-200 px-4 text-gray-700 transition-all hover:bg-gray-300 focus:outline-none"
            >
              <span className="text-base font-semibold">
                {t("verificationForm.uploadIbanCert")}
              </span>
            </label>
            {formik.errors.ibanImage && formik.touched.ibanImage && (
              <div className="text-sm text-red-600">
                {formik.errors.ibanImage}
              </div>
            )}
            {ibanImgPreview && (
              <div className="mt-3 flex w-full items-center gap-2">
                <a
                  href={ibanImgPreview}
                  target="_blank"
                  rel="noreferrer"
                  download={true}
                  className="flex items-center gap-2 rounded-md bg-sidebar px-3 py-1 text-sm text-skyblue"
                >
                  {t("verificationForm.downloadPrevious")}                  <FaDownload className="text-base" />
                </a>
              </div>
            )}
            <span className="mt-1 text-sm">
              {t("verificationForm.allowedImageTypes")}
            </span>
          </div>
        </div>
      </div>

      {/* final submit */}
      <button
        type="submit"
        form="main-form"
        className="text-sideborder-sidebar mx-5 rounded-md border border-sidebar py-2 font-semibold"
      >
        {t("verificationForm.sendRequest")}
      </button>
    </form>
  );
}

export default VerificationForm;
